import { ArrowRight, RefreshCcw, CheckCircle2 } from "lucide-react";
import { Box, Chip, LinearProgress, Typography } from "@mui/material";
import { Button, Card, Skeleton } from "randmarcomps";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import JobsGrid from '../components/grids/JobsGrid';
import ManufacturerRequisitionsGrid from "../components/grids/ManufacturerRequisitionsGrid";
import ButtonWithDialogConfirm from '../components/util/ButtonWithDialogConfirm';
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DocumentHeader } from "../components/util/StyledComponents";
import withManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps {
  store?: RootStore;
  applicationId: string;
}

type InsightCard = {
  id: string;
  label: string;
  value?: React.ReactNode;
  footnote?: React.ReactNode;
  items?: InsightCardItem[];
};

type InsightCardItem = {
  id: string;
  label: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
};

type AccountFieldEntry = {
  key: string;
  label: string;
  value: React.ReactNode;
  description: string;
};

type AccountFieldGroup = {
  id: string;
  label: string;
  entries: AccountFieldEntry[];
};

const cardSurfaceClass = "ops-panel flex h-full flex-col gap-4";

const Requisitions: React.FC<IProps> = ({ store, applicationId }) => {
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      const { documentsStore, jobsStore } = store!;
      await documentsStore.getRequisitions(applicationId);
      await jobsStore.getJobs(applicationId);
      setLoading(false);
    };

    if (store) {
      fetchData();
    }
  }, [applicationId, store]);

  const handleRegenerateDraftPurchases = React.useCallback(async (): Promise<void> => {
    const { documentsStore, snackStore, jobsStore } = store!;

    setLoading(true);

    const success = await documentsStore.regenerateDraftPurchases(applicationId);
    snackStore.showResult(success);

    await jobsStore.getJobs(applicationId);
    setLoading(false);
  }, [applicationId, store]);

  if (!store) {
    return <LinearProgress />;
  }

  const { documentsStore: { requisitions }, manufacturerStore: { manufacturer }, jobsStore: { jobs } } = store;

  const currencyCode = manufacturer?.currencyCode?.toUpperCase() || "USD";
  const currencyFormat = React.useMemo(
    () => ({ ...CurrencyUtils.format, currency: currencyCode }),
    [currencyCode]
  );

  const formatDurationString = React.useCallback((value?: string | number | null, unit?: string) => {
    if (value == null) {
      return null;
    }
    const normalized = value.toString().trim();
    if (!normalized) {
      return null;
    }
    if (unit && /^[\d.]+$/.test(normalized)) {
      return `${normalized} ${unit}`;
    }
    return normalized;
  }, []);

  const jobsToUse = jobs?.filter(j => j.header?.type === "GenerateDraftPurchases");
  const hasJobHistory = !!(jobsToUse && jobsToUse.length);

  const statusMessage = loading ? "Syncing draft purchases…" : "Draft purchases are up to date";
  const shortStatusCopy = loading ? "Syncing draft purchases" : "Draft purchases ready";
  const manufacturerLoading = manufacturer === undefined;
  const manufacturerUnavailable = manufacturer === null;

  const formatCurrencyValue = React.useCallback((value?: number | null) => typeof value === "number"
    ? <FormattedNumber value={value} {...currencyFormat} />
    : null, [currencyFormat]);

  const formatBooleanValue = React.useCallback((value?: boolean | null) => {
    if (typeof value !== "boolean") {
      return null;
    }
    return value ? "Enabled" : "Disabled";
  }, []);

  const normalizeTextValue = React.useCallback((value?: string | null) => {
    if (!value || !value.trim()) {
      return null;
    }
    return value.trim();
  }, []);

  const formatEmailValue = React.useCallback((value?: string | null) => {
    const normalized = normalizeTextValue(value);
    if (!normalized) {
      return null;
    }
    return (
      <a
        href={`mailto:${normalized}`}
        className="text-sm font-semibold text-[var(--color-randmar-purple)] underline-offset-4 transition-colors duration-200 hover:text-[var(--color-randmar-blue)] hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-ring)]"
      >
        {normalized}
      </a>
    );
  }, [normalizeTextValue]);

  const manufacturerAccountFieldGroups = React.useMemo<AccountFieldGroup[]>(() => {

    const automationCadence: AccountFieldGroup = {
      id: "automation-cadence",
      label: "Automation cadence",
      entries: [
        {
          key: "AutoUpdate",
          label: "Auto update",
          value: formatBooleanValue(manufacturer?.autoUpdate),
          description: "Whether stocking rules regenerate automatically."
        },
        {
          key: "AutoReorderDate",
          label: "Auto reorder date",
          value: normalizeTextValue(manufacturer?.reorderDay),
          description: "Planned day for the next automated pass."
        },
      ],
    };

    const spendGuardrails: AccountFieldGroup = {
      id: "spend-guardrails",
      label: "Spend guardrails",
      entries: [
        {
          key: "CreditLimit",
          label: "Credit limit",
          value: formatCurrencyValue(manufacturer?.creditLimit),
          description: "Total purchasing power approved for this account."
        },
        {
          key: "DefaultMinimumOrder",
          label: "Minimum order",
          value: formatCurrencyValue(manufacturer?.defaultMinimumOrder),
          description: "Floor spend for automation-generated POs."
        },
        {
          key: "DefaultMaxInventoryValue",
          label: "Max inventory value",
          value: formatCurrencyValue(manufacturer?.defaultMaxInventoryValue),
          description: "Automation will not exceed this ceiling."
        },
      ],
    };

    const coveragePlanning: AccountFieldGroup = {
      id: "coverage-planning",
      label: "Coverage planning",
      entries: [
        {
          key: "DefaultLeadTime",
          label: "Lead time",
          value: formatDurationString(manufacturer?.defaultLeadTime, "days"),
          description: "Average days between PO and receipt."
        },
        {
          key: "DefaultWeeksOfStocking",
          label: "Weeks of stocking",
          value: formatDurationString(manufacturer?.defaultWeeksOfStocking, "weeks"),
          description: "Target coverage window when generating drafts."
        },
      ],
    };

    return [automationCadence, coveragePlanning, spendGuardrails];
  }, [formatBooleanValue, formatCurrencyValue, formatDurationString, manufacturer, normalizeTextValue]);

  const automationInsights = React.useMemo<InsightCard[]>(() => {
    const creditLimit = typeof manufacturer?.creditLimit === "number" ? manufacturer.creditLimit : null;
    const outstanding = typeof manufacturer?.outstandingBalance === "number" ? manufacturer.outstandingBalance : null;
    const availableCredit = creditLimit !== null && outstanding !== null ? creditLimit - outstanding : null;
    const utilizationPercent = creditLimit && creditLimit > 0 && outstanding !== null
      ? Math.round((outstanding / creditLimit) * 100)
      : null;
    const formattedLimit = creditLimit !== null ? CurrencyUtils.formatNumber(creditLimit, currencyCode) : "";
    const formattedOutstanding = outstanding !== null ? CurrencyUtils.formatNumber(outstanding, currencyCode) : "";
    const automationValue = typeof manufacturer?.autoUpdate === "boolean"
      ? (manufacturer.autoUpdate ? "Auto-update on" : "Manual refresh")
      : null;
    const reorderDayCopy = manufacturer?.reorderDay?.trim();
    const automationFootnote = typeof manufacturer?.autoUpdate === "boolean"
      ? (manufacturer.autoUpdate
        ? (reorderDayCopy ? `Next scheduled run: ${reorderDayCopy}` : "Keeps drafts aligned with live demand.")
        : "Automation is paused; use Regenerate when catalog inputs change.")
      : "Automation status will appear once account data loads.";

    const creditHeadroomCaption = creditLimit !== null && outstanding !== null
      ? (
        utilizationPercent !== null && utilizationPercent > 100
          ? `Over limit by ${CurrencyUtils.formatNumber(Math.abs(availableCredit || 0), currencyCode)}`
          : `${formattedOutstanding} in use (${Math.max(0, Math.min(utilizationPercent ?? 0, 999))}% of ${formattedLimit})`
      )
      : "Set a credit limit to monitor usage.";

    const outstandingCaption = outstanding !== null
      ? "Current amount payable to Randmar."
      : "No outstanding balance reported.";

    const creditLimitFootnote = creditLimit !== null
      ? `Limit set to ${formattedLimit}.`
      : "Add a credit limit to guide draft purchasing.";

    const logisticsEntries: InsightCardItem[] = [
      {
        id: "automation-currency",
        label: "Currency",
        value: manufacturer?.currencyCode?.toUpperCase(),
        caption: "All draft purchases settle in this currency.",
      },
      {
        id: "automation-po-email",
        label: "PO email",
        value: formatEmailValue(manufacturer?.pOEmail),
        caption: "Notifications about generated POs go here.",
      },
    ];

    return [
      {
        id: "automation-status",
        label: "Automation status",
        value: automationValue,
        footnote: automationFootnote,
        items: logisticsEntries,
      },
      {
        id: "credit-utilization",
        label: "Credit utilization",
        items: [
          {
            id: "credit-headroom",
            label: "Credit headroom",
            value: availableCredit !== null ? <FormattedNumber value={availableCredit} {...currencyFormat} /> : null,
            caption: creditHeadroomCaption,
          },
          {
            id: "outstanding-balance",
            label: "Outstanding balance",
            value: outstanding !== null ? <FormattedNumber value={outstanding} {...currencyFormat} /> : null,
            caption: outstandingCaption,
          },
        ],
        footnote: creditLimitFootnote,
      },
    ];
  }, [currencyCode, currencyFormat, formatEmailValue, manufacturer]);

  const renderMetricValue = React.useCallback((value?: React.ReactNode) => {
    if (manufacturerLoading) {
      return <Skeleton aria-hidden className="h-6 w-24 rounded-lg" />;
    }
    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim().length === 0)
    ) {
      return <span className="text-[var(--color-muted-foreground)]">Not set</span>;
    }
    return value;
  }, [manufacturerLoading]);
  const insightsHeadingId = "draft-automation-insights";

  const statusMessageId = "draft-purchases-status";
  const summaryHeadingId = "draft-location-summary";
  const jobsHeadingId = "draft-jobs-heading";
  const requisitionsHeadingId = "draft-table-heading";
  const StatusIcon = loading ? RefreshCcw : CheckCircle2;
  const draftCount = requisitions?.length ?? 0;
  const { locationSummaries, referenceSummaries, totalSummary } = React.useMemo(() => {
    const locationMap = new Map<string, { total: number; count: number }>();
    const referenceMap = new Map<string, { total: number; count: number }>();
    let aggregateTotal = 0;
    let aggregateCount = 0;

    (requisitions || []).forEach(req => {
      const locationKey = req.location?.trim() || "Unassigned";
      const referenceKey = req.reference?.trim() || "No reference";
      const subtotal = req.subTotal || 0;

      aggregateTotal += subtotal;
      aggregateCount += 1;

      const locationStats = locationMap.get(locationKey) || { total: 0, count: 0 };
      locationStats.total += subtotal;
      locationStats.count += 1;
      locationMap.set(locationKey, locationStats);

      const referenceStats = referenceMap.get(referenceKey) || { total: 0, count: 0 };
      referenceStats.total += subtotal;
      referenceStats.count += 1;
      referenceMap.set(referenceKey, referenceStats);
    });

    const mapToArray = (map: Map<string, { total: number; count: number }>) =>
      Array.from(map.entries())
        .map(([label, stats]) => ({
          id: label.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
          label,
          ...stats,
        }))
        .sort((a, b) => b.total - a.total);

    return {
      locationSummaries: mapToArray(locationMap),
      referenceSummaries: mapToArray(referenceMap),
      totalSummary: {
        total: aggregateTotal,
        count: aggregateCount,
      },
    };
  }, [requisitions]);

  const displayedLocationSummaries = locationSummaries;
  const topReferenceSummaries = referenceSummaries.slice(0, 2);
  const totalLocations = displayedLocationSummaries.length;
  const totalDraftValue = totalSummary.total;
  const totalDraftCount = totalSummary.count;
  const hasDrafts = draftCount > 0;
  const showEmptyState = !loading && !hasDrafts;

  const formatDraftCountCopy = (count: number) => `${count} draft PO${count === 1 ? "" : "s"}`;
  const formatLocationCountCopy = (count: number) => `${count} location${count === 1 ? "" : "s"}`;
  const formatReferenceDescription = (label: string, count: number) =>
    label === "No reference"
      ? `${formatDraftCountCopy(count)} without references`
      : `${formatDraftCountCopy(count)} ${label}`;

  const automationStatusCard = automationInsights.find(insight => insight.id === "automation-status");
  const creditUtilizationCard = automationInsights.find(insight => insight.id === "credit-utilization");
  const orderedAccountFieldGroups = [
    manufacturerAccountFieldGroups.find(group => group.id === "automation-cadence"),
    manufacturerAccountFieldGroups.find(group => group.id === "coverage-planning"),
    manufacturerAccountFieldGroups.find(group => group.id === "spend-guardrails"),
  ].filter((group): group is AccountFieldGroup => Boolean(group));

  const renderInsightCard = (insight: InsightCard) => (
    <article key={insight.id} className="ops-metric-card" aria-label={insight.label}>
      <p className="ops-metric-label">{insight.label}</p>
      {insight.value !== undefined && (
        <div className="ops-metric-value ops-metric-value--compact">
          {renderMetricValue(insight.value)}
        </div>
      )}
      {insight.items && insight.items.length > 0 && (
        <div className="flex flex-col gap-4">
          {insight.items.map(item => (
            <div key={item.id} aria-label={item.label} className="flex flex-col gap-1">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-[var(--color-muted-foreground)]">
                {item.label}
              </p>
              <div className="ops-metric-value ops-metric-value--compact">
                {renderMetricValue(item.value)}
              </div>
              {item.caption && (
                <p className="ops-metric-footnote">{item.caption}</p>
              )}
            </div>
          ))}
        </div>
      )}
      {insight.footnote && (
        <p className="ops-metric-footnote">{insight.footnote}</p>
      )}
    </article>
  );

  const renderAccountFieldGroup = (group: AccountFieldGroup) => (
    <article key={group.id} className="ops-metric-card" aria-label={group.label}>
      <p className="ops-metric-label">{group.label}</p>
      <div className="flex flex-col gap-4">
        {group.entries.map(entry => (
          <div key={entry.key} aria-label={entry.label} className="flex flex-col gap-1">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-[var(--color-muted-foreground)]">
              {entry.label}
            </p>
            <div className="ops-metric-value ops-metric-value--compact">
              {renderMetricValue(entry.value)}
            </div>
            <p className="ops-metric-footnote">{entry.description}</p>
          </div>
        ))}
      </div>
    </article>
  );

  return (
    <Box component="main" role="main" aria-busy={loading} className="operations-layout pb-10">
      <Box aria-live="polite" aria-atomic="true" sx={{ mb: 1 }}>
        <LinearProgress
          aria-label="Draft purchase loading progress"
          aria-describedby={statusMessageId}
          variant={loading ? "indeterminate" : "determinate"}
          value={loading ? 0 : 100}
          sx={{
            borderRadius: "var(--surface-radius-pill)",
            height: 4,
            backgroundColor: "var(--color-muted)",
            '& .MuiLinearProgress-bar': {
              borderRadius: "var(--surface-radius-pill)",
              backgroundImage: "linear-gradient(90deg, var(--color-randmar-purple), var(--color-randmar-blue))",
              transitionDuration: "var(--transition-slow)",
            },
          }}
        />
      </Box>

      <Box component="header" role="banner">
        <DocumentHeader
          justifyContent="space-between"
          alignItems="center"
          direction="row"
          sx={{
            flexWrap: { xs: "wrap", md: "nowrap" },
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
          }}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Randmar</p>
            <Typography variant="h1" component="h1" className="text-3xl font-semibold tracking-tight text-slate-900">
              Draft Purchases
            </Typography>
            <Typography variant="subtitle1" component="p" className="text-sm text-slate-500">
              Keep an eye on funding, credit, and automation before generating purchase orders.
            </Typography>
            <Typography id={statusMessageId} variant="body2" component="p" className="sr-only">
              {statusMessage}
            </Typography>
          </div>
          <div className="flex w-full flex-col gap-3 md:w-auto md:items-end">
            <Chip
              role="status"
              aria-live="polite"
              color={loading ? "warning" : "success"}
              variant="outlined"
              icon={<StatusIcon className={`h-4 w-4 ${loading ? "motion-safe:animate-spin" : ""}`} aria-hidden="true" />}
              label={shortStatusCopy}
              sx={{
                alignSelf: "flex-start",
                borderRadius: "var(--surface-radius-pill)",
                fontWeight: 600,
              }}
            />
            <Link
              to={Path.operationsSubSection(applicationId, "PurchaseOrders")}
              aria-label="View current purchase orders"
              className="w-full md:w-auto"
            >
              <Button
                id="ButtonGoToPOs"
                title="View POs"
                className="group w-full justify-center transition-transform duration-200 hover:-translate-x-0.5 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2"
              >
                View Current Purchase Orders{" "}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </DocumentHeader>
      </Box>

      <section aria-labelledby={summaryHeadingId}>
        <Typography variant="h2" component="h2" id={summaryHeadingId} className="sr-only">
          Draft purchase coverage
        </Typography>
        <Card
          role="region"
          aria-labelledby={summaryHeadingId}
          className="ops-panel flex flex-col gap-6"
          data-busy={loading}
          aria-live="polite"
        >
          <div className="ops-metric-grid">
            <article className="ops-metric-card" aria-label="Total draft purchases overview">
              <p className="ops-metric-label">All drafts</p>
              <div className="ops-metric-value">
                {loading
                  ? <Skeleton aria-hidden className="h-10 w-40 rounded-lg" />
                  : <FormattedNumber value={totalDraftValue} {...currencyFormat} />}
              </div>
              <p className="ops-metric-footnote">
                {hasDrafts && totalLocations > 0
                  ? `Across ${formatDraftCountCopy(totalDraftCount)} in ${formatLocationCountCopy(totalLocations)}`
                  : `Across ${formatDraftCountCopy(totalDraftCount)}`}
              </p>
            </article>

            {loading && (
              Array.from({ length: 2 }).map((_, index) => (
                <article key={`draft-skeleton-${index}`} className="ops-metric-card" aria-hidden="true">
                  <p className="ops-metric-label">Location</p>
                  <Skeleton aria-hidden className="h-8 w-32 rounded-lg" />
                  <Skeleton aria-hidden className="h-4 w-24 rounded-lg" />
                </article>
              ))
            )}

            {!loading && hasDrafts && displayedLocationSummaries.map(summary => (
              <article
                key={`location-${summary.id}`}
                className="ops-metric-card"
                aria-label={`${summary.label || "Unassigned"} draft summary`}
              >
                <p className="ops-metric-label">
                  {(summary.label || "Unassigned")} drafts
                </p>
                <div className="ops-metric-value">
                  <FormattedNumber value={summary.total} {...currencyFormat} />
                </div>
                <p className="ops-metric-footnote">
                  {`Across ${formatDraftCountCopy(summary.count)}`}
                </p>
              </article>
            ))}

            {showEmptyState && (
              <article className="ops-metric-card" aria-live="polite">
                <p className="ops-metric-label">Location coverage</p>
                <p className="ops-metric-footnote">
                  Draft purchases will appear here after your next sync.
                </p>
              </article>
            )}

            <article className="ops-metric-card" aria-label="Reference summary">
              <p className="ops-metric-label">References</p>
              {loading ? (
                <div className="space-y-3">
                  <Skeleton aria-hidden className="h-8 w-24 rounded-lg" />
                  <Skeleton aria-hidden className="h-4 w-40 rounded-lg" />
                </div>
              ) : topReferenceSummaries.length > 0 ? (
                <div className="ops-dual-stack">
                  {topReferenceSummaries.map(reference => (
                    <div key={`reference-${reference.id}`}>
                      <div className="ops-metric-value ops-metric-value--compact">
                        <FormattedNumber value={reference.total} {...currencyFormat} />
                      </div>
                      <p className="ops-metric-footnote">
                        {formatReferenceDescription(reference.label, reference.count)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="ops-metric-footnote">
                  References will populate after draft purchases are generated.
                </p>
              )}
            </article>
          </div>
          <div role="region" aria-label="Automation status" className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-3 md:items-end md:text-right">
                {
                  hasJobHistory
                    ? (
                      <a
                        href={`#${jobsHeadingId}`}
                        className="text-sm font-semibold text-[var(--color-randmar-purple)] underline-offset-4 transition-colors duration-200 hover:text-[var(--color-randmar-blue)] hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-ring)]"
                      >
                        Review sync jobs
                      </a>
                    )
                    : (
                      null
                    )
                }
              </div>
            </div>

            {manufacturerUnavailable ? (
              <Typography variant="body2" component="p" className="text-sm text-[var(--color-muted-foreground)]">
                We couldn&apos;t load automation inputs. Refresh to try again.
              </Typography>
            ) : (
              <>
                <div role="region" aria-labelledby={insightsHeadingId} className="space-y-3">
                  <Typography variant="subtitle2" component="h3" id={insightsHeadingId} className="sr-only">
                    Automation summary and account inputs
                  </Typography>
                  <div className="ops-metric-grid" aria-live="polite">
                    {automationStatusCard && renderInsightCard(automationStatusCard)}
                    {orderedAccountFieldGroups.map(group => renderAccountFieldGroup(group))}
                    {creditUtilizationCard && renderInsightCard(creditUtilizationCard)}
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>
      </section>

      {
        jobsToUse && jobsToUse.length > 0 &&
        (
          <section className="space-y-3" aria-labelledby={jobsHeadingId}>
            <div className="flex flex-col gap-1">
              <Typography variant="h2" component="h2" id={jobsHeadingId}>Recent sync activity</Typography>
              <Typography variant="body2" component="p" className="text-[var(--color-muted-foreground)]">
                Monitor automation jobs to spot blockers before drafting purchase orders.
              </Typography>
            </div>
            <JobsGrid jobs={jobsToUse} />
          </section>
        )
      }

      <section className="space-y-3" aria-labelledby={requisitionsHeadingId}>
        <Card
          role="region"
          aria-labelledby={requisitionsHeadingId}
          className={cardSurfaceClass}
          aria-live="polite"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <Typography variant="h2" component="h2" id={requisitionsHeadingId}>Draft purchase details</Typography>
              <Typography variant="body2" component="p" className="text-[var(--color-muted-foreground)]">
                Review recommendations, refine line items, and convert drafts into purchase orders when ready.
              </Typography>
            </div>
            <ButtonWithDialogConfirm
              color="primary"
              onConfirmation={handleRegenerateDraftPurchases}
              buttonTitle={`Regenerate Draft Purchases`}
              text="Regeneration replays automation using your freshest catalog, funding, and demand signals."
              disabled={loading}
            />
          </div>
          <ManufacturerRequisitionsGrid requisitions={requisitions} />
        </Card>
      </section>
    </Box>
  );
}

export default withRoot(withManufacturer(inject("store")(observer(Requisitions))));
